import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, Card, PageHeader, cx } from "../components/ui";
import { formatMoney } from "../lib/format";
import type { Lead, LeadCapture } from "../api/types";

const SOURCES = [
  ["", "Not set"],
  ["walk_in", "Walk in"],
  ["phone", "Phone"],
  ["whatsapp", "WhatsApp"],
  ["instagram", "Instagram"],
  ["website", "Website"],
  ["referral", "Referral"],
  ["google", "Google"],
  ["existing_client", "Existing client"],
  ["other", "Other"],
] as const;

/**
 * Capture an enquiry (FR-6.1) and edit one.
 *
 * Someone rings and asks about a haircut: a name and a phone, what they are
 * asking about, and if they mentioned a time. That is the whole form. The
 * product picker previews the price because the number is about to be said out
 * loud, and the server snapshots it so a later price change cannot rewrite it.
 */
export function LeadFormPage() {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();

  const existing = useQuery({
    queryKey: ["lead", id],
    queryFn: ({ signal }) => api.lead(id!, signal),
    enabled: editing,
  });

  if (editing && existing.isPending) {
    return (
      <>
        <BackLink />
        <PageHeader title="Loading…" />
      </>
    );
  }

  if (editing && existing.isError) {
    return (
      <>
        <BackLink />
        <PageHeader title="Not available" subtitle="That enquiry could not be loaded." />
      </>
    );
  }

  return (
    <>
      <BackLink />
      <PageHeader
        title={editing ? `Edit ${existing.data?.display_name || "enquiry"}` : "Capture a lead"}
        subtitle={
          editing
            ? "The quote recorded here is what the customer was told."
            : "Somebody asked. Capture it before it is forgotten."
        }
      />
      <LeadForm
        lead={existing.data}
        onDone={() => navigate("/customers/leads")}
      />
    </>
  );
}

function BackLink() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate("/customers/leads")}
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> Back to leads
    </button>
  );
}

function LeadForm({
  lead,
  onDone,
}: {
  lead: Lead | undefined;
  onDone: (saved: Lead) => void;
  saved?: (lead: Lead) => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currency = user?.account?.currency ?? "INR";
  const [form, setForm] = useState<LeadCapture>(() => ({
    first_name: lead?.first_name ?? "",
    last_name: lead?.last_name ?? "",
    company_name: lead?.company_name ?? "",
    email: lead?.email ?? "",
    phone: lead?.phone ?? "",
    product: lead?.product ?? null,
    requested_start_at: lead?.requested_start_at
      ? toLocalInput(lead.requested_start_at)
      : null,
    source: lead?.source ?? "",
    notes: lead?.notes ?? "",
  }));
  // The manual deal value is kept as the raw input string so a half-typed
  // number is never coerced to 0. It overrides the product price on save.
  const [dealValue, setDealValue] = useState(lead?.quoted_price?.amount ?? "");

  const options = useQuery({
    queryKey: ["product-options"],
    queryFn: ({ signal }) => api.productOptions(signal),
    staleTime: 5 * 60_000,
  });

  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (body: LeadCapture) => {
      const custom = dealValue.trim();
      // An explicit deal value is saved exactly. Blank means "use the product
      // price" on create, and "clear it" on an edit.
      const payload: LeadCapture = {
        ...body,
        quoted_price: custom ? custom : null,
      };
      return lead
        ? api.updateLead(lead.id, payload)
        : api.captureLead({
            ...payload,
            requested_start_at: payload.requested_start_at
              ? new Date(payload.requested_start_at).toISOString()
              : null,
          });
    },
    onSuccess: (saved) => {
      // Without this the queue renders its cached copy: you capture an enquiry,
      // land back on the list, and the new row is not there. The query is
      // inside its 30s stale window, so navigating does not refetch it.
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-summary"] });
      onDone(saved);
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const fields = error?.fields ?? {};

  function set<K extends keyof LeadCapture>(key: K, value: LeadCapture[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate(form);
  }

  const selected = (options.data ?? []).find((o) => o.id === form.product);

  return (
    <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <h2 className="mb-4 text-sm font-semibold">Who they are</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Text
              id="first_name"
              label="First name"
              autoFocus={!lead}
              value={form.first_name ?? ""}
              onChange={(v) => set("first_name", v)}
              errors={fields.first_name}
            />
            <Text
              id="last_name"
              label="Last name"
              value={form.last_name ?? ""}
              onChange={(v) => set("last_name", v)}
              errors={fields.last_name}
            />
            <div className="sm:col-span-2">
              <Text
                id="phone"
                label="Phone"
                type="tel"
                value={form.phone ?? ""}
                onChange={(v) => set("phone", v)}
                errors={fields.phone}
                hint="The one thing you can always follow up on."
              />
            </div>
            <Text
              id="email"
              label="Email"
              type="email"
              value={form.email ?? ""}
              onChange={(v) => set("email", v)}
              errors={fields.email}
            />
            <Text
              id="company_name"
              label="Company"
              value={form.company_name ?? ""}
              onChange={(v) => set("company_name", v)}
              errors={fields.company_name}
            />
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold">What they asked about</h2>
          <div>
            <label htmlFor="lead-product" className="block text-sm font-medium text-slate-700">
              Interested in
            </label>
            <select
              id="lead-product"
              value={form.product ?? ""}
              onChange={(event) => set("product", event.target.value || null)}
              aria-invalid={fields.product?.length ? true : undefined}
              className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            >
              <option value="" disabled={options.isPending}>
                {options.isPending ? "Loading your products…" : "Not sure yet"}
              </option>
              {(options.data ?? []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                  {option.category ? ` · ${option.category}` : ""}
                </option>
              ))}
            </select>
            {fields.product?.length ? (
              <p role="alert" className="mt-1 text-sm text-rose-600">
                {fields.product.join(" ")}
              </p>
            ) : selected ? (
              <p className="mt-1 text-xs text-slate-600">
                {dealValue.trim()
                  ? "Your deal value is saved instead of the product price."
                  : `Product price ${formatMoney(selected.price)}${
                      selected.effective_duration_minutes
                        ? ` · ${selected.effective_duration_minutes} min`
                        : ""
                    } is recorded when no deal value is entered.`}
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Pick a product to record its price and duration, or enter a deal
                value below.
              </p>
            )}
          </div>
          <div className="mt-4">
            <label htmlFor="lead-deal-value" className="block text-sm font-medium text-slate-700">
              Deal value ({currency})
            </label>
            <input
              id="lead-deal-value"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={dealValue}
              onChange={(e) => setDealValue(e.target.value)}
              placeholder="50000"
              aria-invalid={fields.quoted_price?.length ? true : undefined}
              className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            />
            {fields.quoted_price?.length ? (
              <p role="alert" className="mt-1 text-sm text-rose-600">
                {fields.quoted_price.join(" ")}
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                The exact amount agreed. Leave blank to use the selected product's
                price.
              </p>
            )}
          </div>
          <div className="mt-4">
            <label htmlFor="lead-notes" className="block text-sm font-medium text-slate-700">
              What they said
            </label>
            <textarea
              id="lead-notes"
              rows={3}
              value={form.notes ?? ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Asked for a haircut before the wedding on the 14th."
              className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            />
            {fields.notes?.length ? (
              <p role="alert" className="mt-1 text-sm text-rose-600">
                {fields.notes.join(" ")}
              </p>
            ) : null}
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold">When and where</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="lead-when" className="block text-sm font-medium text-slate-700">
                Tentative time
              </label>
              <input
                id="lead-when"
                type="datetime-local"
                value={form.requested_start_at ?? ""}
                onChange={(e) => set("requested_start_at", e.target.value || null)}
                aria-invalid={fields.requested_start_at?.length ? true : undefined}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              />
              {fields.requested_start_at?.length ? (
                <p role="alert" className="mt-1 text-sm text-rose-600">
                  {fields.requested_start_at.join(" ")}
                </p>
              ) : (
                <p className="mt-1 text-xs text-slate-500">
                  Only if they mentioned one. Nothing is booked.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="lead-source" className="block text-sm font-medium text-slate-700">
                How did they ask?
              </label>
              <select
                id="lead-source"
                value={form.source ?? ""}
                onChange={(e) => set("source", e.target.value)}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              >
                {SOURCES.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
              {fields.source?.length ? (
                <p role="alert" className="mt-1 text-sm text-rose-600">
                  {fields.source.join(" ")}
                </p>
              ) : null}
            </div>
          </div>
        </Card>
      </div>

      <div className="lg:col-span-3">
        {error && !Object.keys(fields).length ? (
          <p role="alert" className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error.message}
          </p>
        ) : null}
        {fields.detail ? (
          <p role="alert" className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {fields.detail.join(" ")}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : lead ? "Save changes" : "Capture lead"}
          </Button>
          <Button onClick={() => navigate("/customers/leads")} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}

/** An ISO instant into the `datetime-local` shape the input expects. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function Text({
  id,
  label,
  value,
  onChange,
  errors,
  hint,
  type = "text",
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  errors?: string[] | undefined;
  hint?: string | undefined;
  type?: string | undefined;
  autoFocus?: boolean | undefined;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={errors?.length ? true : undefined}
        className={cx(
          "mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset placeholder:text-slate-400 focus:ring-2 focus:ring-inset",
          errors?.length ? "ring-rose-400" : "ring-slate-300 focus:ring-slate-900",
        )}
      />
      {errors?.length ? (
        <p role="alert" className="mt-1 text-sm text-rose-600">
          {errors.join(" ")}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}